import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import L from "leaflet";
import {
  GeoJSON,
  Marker,
  useMap,
  useMapEvents,
} from "react-leaflet";

import "./actaLocalMap.css";

const COUNTRY_URL = "/map-data/acta-countries.geojson";
const REGION_URL = "/map-data/acta-regions.geojson";
const EMPTY_COLLECTION = Object.freeze({
  type: "FeatureCollection",
  features: [],
});
const COUNTRY_CENTERS = Object.freeze({
  FR: [46.603354, 1.888334],
  BE: [50.64028, 4.666714],
});

let countriesPromise = null;
let administrativeBasemapPromise = null;

function safeCollection(value) {
  return value?.type === "FeatureCollection" && Array.isArray(value.features)
    ? value
    : EMPTY_COLLECTION;
}

async function fetchCollection(url) {
  const response = await fetch(url, {
    cache: "force-cache",
    credentials: "same-origin",
  });

  if (!response.ok) {
    throw new Error(`Impossible de charger ${url}`);
  }

  return safeCollection(await response.json());
}

function loadCountries() {
  if (!countriesPromise) {
    countriesPromise = fetchCollection(COUNTRY_URL).catch((error) => {
      countriesPromise = null;
      throw error;
    });
  }
  return countriesPromise;
}

function loadAdministrativeBasemap() {
  if (!administrativeBasemapPromise) {
    administrativeBasemapPromise = Promise.all([
      loadCountries(),
      fetchCollection(REGION_URL),
    ])
      .then(([countries, regions]) => ({ countries, regions }))
      .catch((error) => {
        administrativeBasemapPromise = null;
        throw error;
      });
  }
  return administrativeBasemapPromise;
}

function featureName(feature) {
  return String(
    feature?.properties?.name
      || feature?.properties?.shapeName
      || "",
  ).trim();
}

function collectPositions(node, bounds) {
  if (!Array.isArray(node) || node.length === 0) return;

  if (
    node.length >= 2
    && Number.isFinite(Number(node[0]))
    && Number.isFinite(Number(node[1]))
  ) {
    const lng = Number(node[0]);
    const lat = Number(node[1]);
    bounds.minLat = Math.min(bounds.minLat, lat);
    bounds.maxLat = Math.max(bounds.maxLat, lat);
    bounds.minLng = Math.min(bounds.minLng, lng);
    bounds.maxLng = Math.max(bounds.maxLng, lng);
    return;
  }

  node.forEach((child) => collectPositions(child, bounds));
}

function geometryCenter(geometry) {
  const bounds = {
    minLat: Infinity,
    maxLat: -Infinity,
    minLng: Infinity,
    maxLng: -Infinity,
  };

  collectPositions(geometry?.coordinates, bounds);

  if (![bounds.minLat, bounds.maxLat, bounds.minLng, bounds.maxLng].every(Number.isFinite)) {
    return null;
  }

  return [
    (bounds.minLat + bounds.maxLat) / 2,
    (bounds.minLng + bounds.maxLng) / 2,
  ];
}

function collectionLabels(collection, fixedCenters = null) {
  return collection.features
    .map((feature) => {
      const country = feature?.properties?.country;
      return {
        key: feature?.properties?.id || featureName(feature),
        name: featureName(feature),
        center: fixedCenters?.[country] || geometryCenter(feature?.geometry),
      };
    })
    .filter((row) => row.name && row.center);
}

function normalizeCities(cities) {
  const seen = new Set();

  return (Array.isArray(cities) ? cities : [])
    .map((city) => ({
      key: city?.key || city?.id || city?.city || city?.name,
      name: String(city?.city || city?.name || "").trim(),
      lat: Number(city?.lat ?? city?.latitude),
      lng: Number(city?.lng ?? city?.longitude),
    }))
    .filter((city) => {
      if (!city.name || !Number.isFinite(city.lat) || !Number.isFinite(city.lng)) {
        return false;
      }

      const key = `${city.name.toLocaleLowerCase("fr-FR")}:${city.lat.toFixed(4)}:${city.lng.toFixed(4)}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function escapeHtml(value) {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function labelIcon(name, kind) {
  const styles = {
    country: [
      "font-size:11px",
      "font-weight:800",
      "letter-spacing:.08em",
      "text-transform:uppercase",
      "color:var(--do-ink)",
      "background:color-mix(in srgb,var(--do-bg-solid) 86%,transparent)",
      "border:1px solid var(--do-border)",
      "padding:3px 7px",
      "border-radius:999px",
    ],
    region: [
      "font-size:10px",
      "font-weight:700",
      "color:var(--do-muted)",
      "background:color-mix(in srgb,var(--do-bg-solid) 72%,transparent)",
      "padding:2px 5px",
      "border-radius:7px",
    ],
    city: [
      "font-size:10px",
      "font-weight:700",
      "color:var(--do-ink-soft)",
      "background:color-mix(in srgb,var(--do-bg-solid) 82%,transparent)",
      "border:1px solid var(--do-border)",
      "padding:2px 5px",
      "border-radius:7px",
    ],
  }[kind];
  const transform = kind === "city"
    ? "translate(-50%,10px)"
    : "translate(-50%,-50%)";

  return L.divIcon({
    className: "acta-local-map-label-icon",
    iconSize: [0, 0],
    iconAnchor: [0, 0],
    html: `<span style="display:inline-block;transform:${transform};white-space:nowrap;pointer-events:none;box-shadow:0 4px 14px rgba(15,23,42,.06);${styles.join(";")}">${escapeHtml(name)}</span>`,
  });
}

function LocalAttribution() {
  const map = useMap();

  useEffect(() => {
    const attribution = "Fond local Acta · limites geoBoundaries";
    map.attributionControl?.addAttribution(attribution);

    return () => {
      map.attributionControl?.removeAttribution(attribution);
    };
  }, [map]);

  return null;
}

function useZoomLevel() {
  const map = useMapEvents({
    zoomend: () => setZoom(map.getZoom()),
  });
  const [zoom, setZoom] = useState(() => map.getZoom());
  return zoom;
}

export default function ActaLocalBasemap({
  cities = [],
  variant = "administrative",
}) {
  const minimal = variant === "minimal";
  const [basemap, setBasemap] = useState({
    countries: EMPTY_COLLECTION,
    regions: EMPTY_COLLECTION,
  });
  const zoom = useZoomLevel();

  useEffect(() => {
    let mounted = true;
    const loader = minimal
      ? loadCountries().then((countries) => ({
          countries,
          regions: EMPTY_COLLECTION,
        }))
      : loadAdministrativeBasemap();

    loader
      .then((payload) => {
        if (mounted) setBasemap(payload);
      })
      .catch(() => {
        if (mounted) {
          setBasemap({
            countries: EMPTY_COLLECTION,
            regions: EMPTY_COLLECTION,
          });
        }
      });

    return () => {
      mounted = false;
    };
  }, [minimal]);

  const countryLabels = useMemo(
    () => minimal
      ? []
      : collectionLabels(basemap.countries, COUNTRY_CENTERS),
    [basemap.countries, minimal],
  );
  const regionLabels = useMemo(
    () => minimal ? [] : collectionLabels(basemap.regions),
    [basemap.regions, minimal],
  );
  const cityLabels = useMemo(
    () => minimal ? [] : normalizeCities(cities),
    [cities, minimal],
  );

  return (
    <>
      {!minimal ? (
        <GeoJSON
          key={`regions-${basemap.regions.features.length}`}
          data={basemap.regions}
          interactive={false}
          style={() => ({
            color: "var(--do-border-strong)",
            weight: 1,
            opacity: 0.82,
            fillColor: "var(--do-bg-2)",
            fillOpacity: 0.68,
          })}
        />
      ) : null}

      <GeoJSON
        key={`countries-${basemap.countries.features.length}-${variant}`}
        data={basemap.countries}
        interactive={false}
        style={() => minimal
          ? {
              stroke: false,
              fillColor: "var(--do-bg-2)",
              fillOpacity: 0.72,
            }
          : {
              color: "var(--do-accent)",
              weight: 1.8,
              opacity: 0.62,
              fillOpacity: 0,
            }}
      />

      {!minimal && zoom <= 6
        ? countryLabels.map((label) => (
          <Marker
            key={`country-${label.key}`}
            position={label.center}
            icon={labelIcon(label.name, "country")}
            interactive={false}
          />
        ))
        : null}

      {!minimal && zoom >= 5 && zoom <= 8
        ? regionLabels.map((label) => (
          <Marker
            key={`region-${label.key}`}
            position={label.center}
            icon={labelIcon(label.name, "region")}
            interactive={false}
          />
        ))
        : null}

      {!minimal && zoom >= 6
        ? cityLabels.map((city) => (
          <Marker
            key={`city-${city.key}-${city.lat}-${city.lng}`}
            position={[city.lat, city.lng]}
            icon={labelIcon(city.name, "city")}
            interactive={false}
          />
        ))
        : null}

      <LocalAttribution />
    </>
  );
}
